import React, { useState, useEffect, useContext, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { X, Image } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { uploadPostMedia } from '../../services/storageService';
import SecurityUtils from '../../utils/security';
import { validateVideoDuration, generateVideoThumbnail, compressVideo } from '../../utils/media';
import { GAME_TYPES } from '../../utils/constants';

const GameRecapView = () => {
  const { activeGame, setActiveGame, selectedGameId, users, setUsers, currentUser, setCurrentUser, events, setView, conversations, showAlert, showConfirm } = useContext(AppContext);
  const [game, setGame] = useState(activeGame);
  const [sharingAll, setSharingAll] = useState(false);
  const [sharingMy, setSharingMy] = useState(false);
  const [sharedAll, setSharedAll] = useState(false);
  const [sharedMy, setSharedMy] = useState(false);
  const [sharingChat, setSharingChat] = useState(false);
  const [sharedChat, setSharedChat] = useState(false);
  const [weather, setWeather] = useState('sunny');
  const [handicapModal, setHandicapModal] = useState(null); // { newIndex, roundCount }
  const [handicapNotice, setHandicapNotice] = useState(null); // { roundsNeeded }
  const [updatingHandicap, setUpdatingHandicap] = useState(false);
  const [statsImages, setStatsImages] = useState([]);
  const [statsImageFiles, setStatsImageFiles] = useState([]);
  const [statsVideoFile, setStatsVideoFile] = useState(null);
  const [statsVideoPreview, setStatsVideoPreview] = useState(null);
  const [statsUploadProgress, setStatsUploadProgress] = useState(0);
  const statsMediaInputRef = useRef(null);
  const handleStatsMediaSelect = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      if (isVideo) {
        const validation = SecurityUtils.validateFile(file, { maxSize: 50 * 1024 * 1024 });
        if (!validation.valid) { showAlert(`Video validation failed: ${validation.error}`, 'Error'); continue; }
        const validDuration = await validateVideoDuration(file, 15);
        if (!validDuration) { showAlert('Video must be 15 seconds or less.', 'Error'); continue; }
        setStatsVideoFile(file);
        setStatsVideoPreview(URL.createObjectURL(file));
      } else {
        const validation = SecurityUtils.validateFile(file, { maxSize: 10 * 1024 * 1024 });
        if (!validation.valid) { showAlert(`Image validation failed: ${validation.error}`, 'Error'); continue; }
        if (validation.type !== 'image') { showAlert('Please select valid image files only.', 'Error'); continue; }
        if (statsImageFiles.length + statsImages.length >= 8) { showAlert('Maximum 8 images allowed.', 'Warning'); break; }
        const reader = new FileReader();
        reader.onloadend = () => {
          setStatsImages(prev => [...prev, reader.result]);
          setStatsImageFiles(prev => [...prev, file]);
        };
        reader.readAsDataURL(file);
      }
    }
    e.target.value = '';
  };

  const uploadStatsMedia = async (postData, postId) => {
    const mediaFiles = [];
    let videoFileToUpload = statsVideoFile;
    let thumbnailBlob = null;

    if (videoFileToUpload) {
      setStatsUploadProgress(-1);
      const [compressed, thumb] = await Promise.all([
        compressVideo(videoFileToUpload),
        generateVideoThumbnail(videoFileToUpload),
      ]);
      videoFileToUpload = compressed;
      thumbnailBlob = thumb;
      mediaFiles.push(videoFileToUpload);
    }
    if (statsImageFiles.length > 0) mediaFiles.push(...statsImageFiles);
    if (thumbnailBlob) mediaFiles.push(new File([thumbnailBlob], 'thumb.jpg', { type: 'image/jpeg' }));

    if (mediaFiles.length > 0) {
      setStatsUploadProgress(0);
      const uploaded = await uploadPostMedia(postId, mediaFiles, currentUser.id, (p) => setStatsUploadProgress(p));
      const thumbUpload = uploaded.find(u => u.name === 'thumb.jpg' && u.type === 'image');
      const imageUrls = uploaded.filter(u => u.type === 'image' && u.name !== 'thumb.jpg').map(u => u.url);
      const videoUrl = uploaded.find(u => u.type === 'video');
      if (imageUrls.length > 0) { postData.images = imageUrls; postData.imagePreviews = imageUrls; }
      if (videoUrl) { postData.video = { filename: videoUrl.name, url: videoUrl.url, thumbnail: thumbUpload?.url || null }; }
    }
    return postData;
  };

  const clearStatsMedia = () => {
    setStatsImages([]); setStatsImageFiles([]); setStatsVideoFile(null); setStatsVideoPreview(null); setStatsUploadProgress(0);
  };

  useEffect(() => {
    if (!game && selectedGameId) {
      firestoreService.getGame(selectedGameId).then(g => { if (g) setGame(g); });
    }
  }, [selectedGameId, game]);

  // Run handicap calculation only the first time a user views the recap of a completed game.
  useEffect(() => {
    if (!activeGame || activeGame.status !== 'completed') return;
    if (currentUser.handicapPromptedGameIds?.includes(activeGame.id)) return;

    const getDifferentialsToUse = (count) => {
      if (count >= 20) return 8;
      if (count === 19) return 7;
      if (count >= 17) return 6;
      if (count >= 15) return 5;
      if (count >= 12) return 4;
      return 3; // 10–11
    };

    const getScoreDifferential = (g, userId) => {
      const score = Object.values(g.holes || {}).reduce((sum, h) => sum + (h.scores?.[userId] || 0), 0);
      if (score === 0) return null;
      const courseRating = g.selectedCourseData?.courseRating;
      const slopeRating = g.selectedCourseData?.slopeRating;
      if (courseRating && slopeRating) {
        return (score - courseRating) * (113 / slopeRating);
      }
      const totalPar = Object.values(g.holes || {}).reduce((sum, h) => sum + (h.par || 4), 0);
      return score - totalPar;
    };

    const markPrompted = (gameId) => {
      firestoreService.markHandicapPrompted(currentUser.id, gameId).catch(err =>
        console.error('Error marking handicap prompted:', err)
      );
      setCurrentUser(prev => ({
        ...prev,
        handicapPromptedGameIds: [...(prev.handicapPromptedGameIds || []), gameId],
      }));
    };

    const calcHandicap = async () => {
      try {
        const allGames = await firestoreService.getUserCompletedGames(currentUser.id);
        const differentials = allGames
          .map(g => getScoreDifferential(g, currentUser.id))
          .filter(d => d !== null);

        const roundCount = differentials.length;
        if (roundCount < 10) {
          setHandicapNotice({ roundsNeeded: 10 - roundCount });
          markPrompted(activeGame.id);
          return;
        }

        const pool = differentials.slice(0, 20); // most recent 20
        const numToUse = getDifferentialsToUse(pool.length);
        const sorted = [...pool].sort((a, b) => a - b);
        const best = sorted.slice(0, numToUse);
        const avg = best.reduce((sum, d) => sum + d, 0) / best.length;
        const newIndex = Math.round(avg * 0.96 * 10) / 10 * -1;

        setHandicapModal({ newIndex, roundCount });
        markPrompted(activeGame.id);
      } catch (err) {
        console.error('Handicap calculation error:', err);
      }
    };

    calcHandicap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAcceptHandicap = async () => {
    setUpdatingHandicap(true);
    try {
      await firestoreService.updateUser(currentUser.id, { handicap: handicapModal.newIndex });
      setCurrentUser(prev => ({ ...prev, handicap: handicapModal.newIndex }));
      setHandicapModal(null);
    } catch (err) {
      console.error('Error updating handicap:', err);
    }
    setUpdatingHandicap(false);
  };

  if (!game) return <div className="max-w-2xl mx-auto p-4 text-center text-white">Loading game...</div>;

  const gameTypeInfo = GAME_TYPES.find(g => g.id === game.gameType);
  const holes = game.holes || {};
  const isScorecard = game.source === 'scorecard';

  const getTotalScore = (userId) => Object.values(holes).reduce((sum, h) => sum + (h.scores?.[userId] || 0), 0);
  const getTotalPar = () => Object.values(holes).reduce((sum, h) => sum + (h.par || 4), 0);

  const getBestHole = (userId) => {
    let best = null;
    Object.entries(holes).forEach(([num, h]) => {
      const score = h.scores?.[userId] || 0;
      if (score > 0) {
        const diff = score - (h.par || 4);
        if (best === null || diff < best.diff) {
          best = { hole: num, score, par: h.par || 4, diff };
        }
      }
    });
    return best;
  };

  const sortedPlayers = [...game.players].sort((a, b) => {
    const aTotal = getTotalScore(a.userId);
    const bTotal = getTotalScore(b.userId);
    if (aTotal === 0 && bTotal === 0) return 0;
    if (aTotal === 0) return 1;
    if (bTotal === 0) return -1;
    return aTotal - bTotal;
  });

  const handleShareToFeed = async () => {
    if (!await showConfirm('Share game stats to the feed?', 'Share Stats', 'Share')) return;
    setSharingAll(true);
    try {
      const dateStr = new Date(game.startedAt).toLocaleDateString();
      const isMultiPlayer = game.players.length > 1;
      const title = isMultiPlayer ? `${game.courseName} Stats! ${dateStr}` : `My ${game.courseName} Stats! ${dateStr}`;

      const finalScores = {};
      game.players.forEach(p => { finalScores[p.userId] = getTotalScore(p.userId); });

      const postId = `p${Date.now()}`;
      let postData = {
        userId: currentUser.id,
        type: 'gameRecap',
        content: title,
        gameRecap: {
          gameType: game.gameType,
          courseName: game.courseName,
          players: game.players,
          finalScores,
          totalPar: getTotalPar(),
          totalHoles: game.totalHoles,
          winner: sortedPlayers[0]?.userId,
          winnerName: sortedPlayers[0]?.name,
          weather,
        }
      };

      postData = await uploadStatsMedia(postData, postId);
      await firestoreService.createPost(postData);
      clearStatsMedia();
      setSharingAll(false);
      setSharedAll(true);
    } catch (err) {
      console.error('Error sharing to feed:', err);
      setSharingAll(false);
    }
  };

  const handleShareMyStats = async () => {
    if (!await showConfirm('Share your stats to the feed?', 'Share Stats', 'Share')) return;
    setSharingMy(true);
    try {
      const dateStr = new Date(game.startedAt).toLocaleDateString();
      const myScore = getTotalScore(currentUser.id);
      const diff = myScore - getTotalPar();
      const bestHole = getBestHole(currentUser.id);

      const postId = `p${Date.now()}`;
      let postData = {
        userId: currentUser.id,
        type: 'gameRecap',
        content: `My ${game.courseName} Stats! ${dateStr}`,
        gameRecap: {
          gameType: game.gameType,
          courseName: game.courseName,
          players: [{ userId: currentUser.id, name: currentUser.name }],
          finalScores: { [currentUser.id]: myScore },
          totalPar: getTotalPar(),
          totalHoles: game.totalHoles,
          myScore: myScore,
          myDiff: diff,
          bestHole: bestHole,
          isIndividual: true,
          weather,
        }
      };

      postData = await uploadStatsMedia(postData, postId);
      await firestoreService.createPost(postData);
      clearStatsMedia();
      setSharingMy(false);
      setSharedMy(true);
    } catch (err) {
      console.error('Error sharing individual stats:', err);
      setSharingMy(false);
    }
  };

  const handleShareToGroupChat = async () => {
    setSharingChat(true);
    try {
      const event = events.find(e => e.id === game.eventId);
      if (!event?.groupChatId) { showAlert('No group chat for this event.', 'Error'); setSharingChat(false); return; }

      const dateStr = new Date(game.startedAt).toLocaleDateString();
      let summary = `🏌️ Game Recap: ${gameTypeInfo?.name} at ${game.courseName} (${dateStr})\n`;
      sortedPlayers.forEach((p, i) => {
        const total = getTotalScore(p.userId);
        const diff = total - getTotalPar();
        summary += `${i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`} ${p.name}: ${total} (${diff >= 0 ? '+' : ''}${diff})\n`;
      });

      await firestoreService.sendMessage({
        conversationId: event.groupChatId,
        senderId: currentUser.id,
        content: summary,
      });
      setSharingChat(false);
      setSharedChat(true);
    } catch (err) {
      console.error('Error sharing to group chat:', err);
      setSharingChat(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="bg-black rounded-lg border border-gray-700 p-6 mb-4">
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-3xl">{gameTypeInfo?.emoji}</span>
          <div>
            <h2 className="text-2xl font-bold text-white">{gameTypeInfo?.name} Recap</h2>
            <p className="text-gray-400">{game.courseName} | {new Date(game.startedAt).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Winner Banner */}
        {!isScorecard && sortedPlayers[0] && getTotalScore(sortedPlayers[0].userId) > 0 && (
          <div className="bg-[#16a34a] bg-opacity-20 border border-[#16a34a] rounded-lg p-4 mb-4 text-center">
            <p className="text-[#16a34a] text-sm font-medium">Winner</p>
            <p className="text-white text-xl font-bold">🏆 {sortedPlayers[0].name}</p>
            <p className="text-gray-300">{getTotalScore(sortedPlayers[0].userId)} ({getTotalScore(sortedPlayers[0].userId) - getTotalPar() >= 0 ? '+' : ''}{getTotalScore(sortedPlayers[0].userId) - getTotalPar()})</p>
          </div>
        )}

        {/* Personal Score Summary (solo scorecard) */}
        {isScorecard && getTotalScore(currentUser.id) > 0 && (
          <div className="bg-gray-900 rounded-lg p-4 mb-4 text-center">
            <p className="text-gray-400 text-sm mb-1">Your Score</p>
            <p className="text-4xl font-bold text-white">{getTotalScore(currentUser.id)}</p>
            <p className={`text-lg font-bold mt-1 ${getTotalScore(currentUser.id) - getTotalPar() <= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {getTotalScore(currentUser.id) - getTotalPar() >= 0 ? '+' : ''}{getTotalScore(currentUser.id) - getTotalPar()} to par
            </p>
          </div>
        )}

        {/* Handicap notice — not enough rounds */}
        {handicapNotice && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 mb-4 text-sm text-gray-400">
            Not enough rounds to calculate an approximate handicap index. {handicapNotice.roundsNeeded} more {handicapNotice.roundsNeeded === 1 ? 'round' : 'rounds'} needed.
          </div>
        )}

        {/* Scorecard */}
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-gray-400 p-2">Hole</th>
                {Array.from({ length: game.totalHoles }, (_, i) => i + 1).map(h => (
                  <th key={h} className="text-center text-gray-400 p-1 min-w-[30px]">{h}</th>
                ))}
                <th className="text-center text-gray-400 p-2">Tot</th>
              </tr>
              <tr className="border-b border-gray-700">
                <td className="text-gray-500 p-2 text-xs">Par</td>
                {Array.from({ length: game.totalHoles }, (_, i) => i + 1).map(h => (
                  <td key={h} className="text-center text-gray-500 p-1 text-xs">{holes[String(h)]?.par || 4}</td>
                ))}
                <td className="text-center text-gray-500 p-2 text-xs">{getTotalPar()}</td>
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map(player => (
                <tr key={player.userId} className="border-b border-gray-800">
                  <td className="text-white p-2 font-medium whitespace-nowrap">{player.name}</td>
                  {Array.from({ length: game.totalHoles }, (_, i) => i + 1).map(h => {
                    const score = holes[String(h)]?.scores?.[player.userId] || 0;
                    const par = holes[String(h)]?.par || 4;
                    const diff = score - par;
                    return (
                      <td key={h} className={`text-center p-1 ${score === 0 ? 'text-gray-500' : diff < 0 ? 'text-green-400' : diff > 0 ? 'text-red-400' : 'text-white'}`}>
                        {score || '-'}
                      </td>
                    );
                  })}
                  <td className="text-center p-2 font-bold text-white">{getTotalScore(player.userId) || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Best Holes */}
        <div className="mb-4">
          <h4 className="text-white font-bold mb-2">Highlights</h4>
          <div className="grid grid-cols-2 gap-2">
            {sortedPlayers.map(player => {
              const best = getBestHole(player.userId);
              if (!best) return null;
              return (
                <div key={player.userId} className="bg-gray-900 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-xs">{player.name}'s Best</p>
                  <p className="text-white font-bold">Hole {best.hole}: {best.score}</p>
                  <p className="text-xs text-gray-400">({best.diff >= 0 ? '+' : ''}{best.diff} to par)</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Attach Media */}
        {!sharedAll && !sharedMy && (
          <div className="mb-4">
            <input
              ref={statsMediaInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleStatsMediaSelect}
              className="hidden"
            />
            <button
              onClick={() => statsMediaInputRef.current?.click()}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 text-sm"
            >
              <Image className="w-4 h-4" />
              <span>Attach Photos / Video</span>
            </button>

            {statsImages.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {statsImages.map((src, i) => (
                  <div key={i} className="relative">
                    <img src={src} alt="" className="w-20 h-20 rounded-lg object-cover" />
                    <button onClick={() => { setStatsImages(prev => prev.filter((_, idx) => idx !== i)); setStatsImageFiles(prev => prev.filter((_, idx) => idx !== i)); }} className="absolute -top-1 -right-1 bg-red-600 text-pure-white rounded-full w-5 h-5 flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {statsVideoPreview && (
              <div className="relative mt-3 inline-block">
                <video src={statsVideoPreview} className="w-40 h-28 rounded-lg object-cover" muted />
                <button onClick={() => { setStatsVideoFile(null); setStatsVideoPreview(null); }} className="absolute -top-1 -right-1 bg-red-600 text-pure-white rounded-full w-5 h-5 flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {statsUploadProgress !== 0 && (
              <div className="mt-2">
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-[#16a34a] h-2 rounded-full transition-all" style={{ width: `${statsUploadProgress < 0 ? 100 : statsUploadProgress}%` }} />
                </div>
                <p className="text-xs text-gray-400 mt-1">{statsUploadProgress < 0 ? 'Compressing video...' : `Uploading ${statsUploadProgress}%`}</p>
              </div>
            )}
          </div>
        )}

        {/* Weather selector */}
        {!sharedAll && !sharedMy && (
          <div className="mb-4">
            <p className="text-sm font-medium text-white mb-2">Weather conditions</p>
            <div className="flex space-x-2">
              {[{ value: 'sunny', label: '☀️ Sunny' }, { value: 'cloudy', label: '☁️ Cloudy' }, { value: 'rainy', label: '🌧️ Rainy' }, { value: 'windy', label: '💨 Windy' }].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setWeather(opt.value)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${weather === opt.value ? 'bg-[#16a34a] border-[#16a34a] text-pure-white' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Share Buttons */}
        <div className="space-y-2">
          <div className="flex space-x-2">
            {game.players?.length > 1 && game.createdBy === currentUser.id && (
              <button onClick={handleShareToFeed} disabled={sharingAll || sharedAll} className={`flex-1 py-3 rounded-lg font-bold disabled:opacity-50 ${sharedAll ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-[#16a34a] text-pure-white hover:bg-[#15803d]'}`}>
                {sharedAll ? 'Stats Already Shared' : sharingAll ? 'Sharing...' : 'Share All Stats'}
              </button>
            )}
            {game.players?.some(p => p.userId === currentUser.id) && (
              <button onClick={handleShareMyStats} disabled={sharingMy || sharedMy} className={`flex-1 py-3 rounded-lg font-bold disabled:opacity-50 ${sharedMy ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-blue-600 text-pure-white hover:bg-blue-700'}`}>
                {sharedMy ? 'Stats Already Shared' : sharingMy ? 'Sharing...' : 'Share My Stats'}
              </button>
            )}
          </div>
          {!isScorecard && game.createdBy === currentUser.id && (
            <button onClick={handleShareToGroupChat} disabled={sharingChat || sharedChat} className={`w-full py-3 rounded-lg font-bold ${sharedChat ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-600'} disabled:opacity-50`}>
              {sharedChat ? 'Already Shared to Group Chat' : sharingChat ? 'Sharing...' : 'Share to Group Chat'}
            </button>
          )}
          {isScorecard && (
            <button onClick={() => setView('my-coach')} className="w-full py-3 rounded-lg font-bold bg-gray-700 text-white hover:bg-gray-600">
              View My Coach
            </button>
          )}
        </div>
      </div>

      <button onClick={() => { setActiveGame(null); setView(isScorecard ? 'feed' : 'events'); }} className="w-full py-2 text-gray-400 hover:text-white text-center">
        {isScorecard ? 'Done' : 'Back to Events'}
      </button>

      {/* Approximate handicap index modal */}
      {handicapModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-white font-bold text-lg mb-2">Approximate Handicap Index</h3>
            <p className="text-gray-400 text-sm mb-4">
              Based on {handicapModal.roundCount} completed {handicapModal.roundCount === 1 ? 'round' : 'rounds'}, your approximate handicap index is:
            </p>
            <p className="text-5xl font-bold text-white text-center mb-1">{handicapModal.newIndex}</p>
            <p className="text-gray-500 text-xs text-center mb-6">Approximate index</p>
            <p className="text-gray-400 text-sm mb-6">
              Would you like to update your profile with this handicap index?
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setHandicapModal(null)}
                className="flex-1 py-3 bg-gray-700 text-white rounded-lg font-bold hover:bg-gray-600"
              >
                No thanks
              </button>
              <button
                onClick={handleAcceptHandicap}
                disabled={updatingHandicap}
                className="flex-1 py-3 bg-[#16a34a] text-pure-white rounded-lg font-bold hover:bg-[#15803d] disabled:opacity-50"
              >
                {updatingHandicap ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== GAME RECAP CARD (for feed) ====================

export default GameRecapView;
